import React, { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { BasicProductFragment, ProductFragment } from '@/gql';
import analytics from '@/lib/analytics';
import { isProductFragment } from '@/lib/gql';
import { buildSortedProductAdditionalImages } from '@/lib/product';
import { buildListingRoute, productIdFromUrl } from '@/lib/navigation';
import { useStoreData } from '@/hooks/use-store-data';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import ProductVisualDisplay from '@/components/ProductVisualDisplay';
import ProductTile from '@/components/ProductTile';
// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/virtual';

// import required modules
import { Zoom, Virtual, Pagination, Navigation } from 'swiper/modules';

import styles from './index.module.css';

/**
 * `Carousel` is a versatile component for displaying a series of products in a sliding carousel format.
 * It supports different types of product data and adapts to display either `BasicProductFragment` or `ProductFragment`.
 *
 * The component is designed to be reusable and can be integrated into different parts of the application where
 * a carousel display is needed. It handles user interactions such as clicking on a product and navigation between carousel slides.
 * Analytics tracking is integrated for viewing item lists and selecting individual items.
 *
 * Props:
 * @param {string} name - Display name for the carousel, typically the collection name.
 * @param {string} carouselClassName - Custom CSS class for styling the carousel.
 * @param {BasicProductFragment[] | ProductFragment[]} productData - Array of products to display in the carousel.
 * @param {boolean} loading - Indicates if the carousel is in a loading state.
 * @param {Function} onProductClicked - Callback function when a product is clicked.
 * @param {string} prevButtonId - ID for the previous button in carousel navigation.
 * @param {string} nextButtonId - ID for the next button in carousel navigation.
 * @param {string} viewAllUrl - Url for the collection, used in navigation.
 * @param {string} placement - Placement identifier for analytics tracking.
 *
 */
interface CarouselProps {
  name?: string;
  carouselClassName?: string;
  productData?: readonly BasicProductFragment[] | readonly ProductFragment[];
  loading?: boolean; // eslint-disable-line react/no-unused-prop-types
  onProductClicked?: () => void;
  prevButtonId?: string;
  nextButtonId?: string;
  viewAllUrl?: string;
  placement: string;
  navNextClass?: string;
  navPrevClass?: string;
}
const Carousel: React.FC<CarouselProps> = ({
  name,
  productData,
  onProductClicked,
  carouselClassName,
  prevButtonId,
  nextButtonId,
  placement,
  viewAllUrl = '',
  navPrevClass,
  navNextClass,
}) => {
  const { storeData } = useStoreData();
  const [hasPrev, setHasPrev] = useState(false);
  const [hasNext, setHasNext] = useState(true);

  useEffect(() => {
    const productCount = productData?.length || 0;
    setHasPrev(productCount < 1);
    setHasNext(productCount > 4);
  }, [productData]);

  const onLinkClick = useCallback(
    (item: BasicProductFragment, index: number) => {
      if (onProductClicked) {
        onProductClicked();
      }
      const payload = {
        item,
        position: index + 1,
        placement,
        source: name ?? '',
      };
      analytics.track('select_item', payload, storeData);
    },
    [onProductClicked, storeData, placement, name]
  );

  const viewAllButton = () => (
    <Link href={viewAllUrl}>
      <h5 className={styles.viewFullCollectionLink}>
        View full collection <ChevronRightIcon className={styles.chevronRightIcon} />
      </h5>
    </Link>
  );

  const renderProduct = useCallback(
    (product: BasicProductFragment | ProductFragment, index: number) => {
      if (isProductFragment(product)) {
        // Render ProductFragment
        return <ProductTile key={product.id} product={product} productTileContainerClass={styles.productTilePadding} />;
      } else {
        // Render BasicProductFragment
        const orderedImages = buildSortedProductAdditionalImages(product);
        return (
          <ProductVisualDisplay
            productId={String(product.id) || ''}
            productTitle={product.name || ''}
            productType={product.productName || ''}
            images={orderedImages}
            listingUrl={buildListingRoute({
              listingSlug: product.listingSlug ?? undefined,
              productId: productIdFromUrl(product.url || ''),
            })}
            price={product.price || ''}
            onProductClicked={() => onLinkClick(product, index)}
            productTileContainerClass={styles.productTilePadding}
          />
        );
      }
    },
    [onLinkClick]
  );

  const currentPrevButtonClass = hasPrev ? `${navPrevClass} ${styles.navPrev}` : styles.displayNone;
  const currentNextButtonClass = hasNext ? `${navNextClass} ${styles.navNext}` : styles.displayNone;

  if (!productData || productData.length === 0) return null;

  return (
    <div className={carouselClassName}>
      <div className={styles.carouselContainer}>
        <h2 id='you-might-also-like'>{name}</h2>
        {viewAllUrl.length > 0 && productData?.length > 4 && viewAllButton()}
      </div>

      <div className={styles.carouselRelative}>
        <div className={styles.carouselWFull}>
          <Swiper
            slidesPerView={4}
            spaceBetween={20}
            navigation={{
              nextEl: nextButtonId ? `#${nextButtonId}` : undefined,
              prevEl: prevButtonId ? `#${prevButtonId}` : undefined,
            }}
            zoom
            pagination={{
              clickable: true,
            }}
            breakpoints={{
              200: {
                slidesPerView: 2,
                spaceBetween: 20,
              },
              768: {
                slidesPerView: 3,
                spaceBetween: 20,
              },
              1024: {
                slidesPerView: 4,
                spaceBetween: 20,
              },
            }}
            virtual
            modules={[Zoom, Virtual, Pagination, Navigation]}
            onSlideChange={(swiper) => {
              const isAtStart = swiper.isBeginning;
              const isAtEnd = swiper.isEnd;
              setHasPrev(!isAtStart);
              setHasNext(!isAtEnd);
              analytics.track('recommended_products_touched', { placement, source: name });
            }}
            className={styles.carouselList}
            data-testid='custom-collection-carousel'
          >
            {productData?.map((product, index) => (
              <SwiperSlide key={product.id} virtualIndex={Number(product.id)}>
                {renderProduct(product, index)}
              </SwiperSlide>
            ))}
          </Swiper>
          <ChevronLeftIcon
            data-testid={prevButtonId}
            id={prevButtonId}
            className={currentPrevButtonClass}
            aria-hidden='true'
          />
          <ChevronRightIcon
            data-testid={nextButtonId}
            id={nextButtonId}
            className={currentNextButtonClass}
            aria-hidden='true'
          />
        </div>
      </div>
    </div>
  );
};

export default Carousel;
